<script setup>
import { ref, watch } from 'vue'

/**
 * The component's props.
 */
const props = defineProps({
    /**
     * Determine if the field is editable.
     *
     * @type {Boolean}
     */
    isEditable: {
        type: Boolean,
        default: false,
    },

    /**
     * The value of the field.
     *
     * @type {String}
     */
    value: {
        type: String,
        required: true,
    },
})

/**
 * The component's emits.
 */
const emits = defineEmits(['save', 'cancel'])

/**
 * The local refs.
 */
const element = ref(null)

/**
 * The save event handler.
 *
 * @return {void}
 */
const onSave = () => {
    const value = element.value.innerText.trim()

    if (value === props.value) {
        return emits('cancel')
    }

    emits('save', value)
}

/**
 * The cancel event handler.
 *
 * @return {void}
 */
const onCancel = () => {
    element.value.innerText = props.value

    emits('cancel')
}

watch(
    () => props.isEditable,
    (value) => {
        if (!value) {
            return
        }

        element.value.contentEditable = true
        element.value.focus()
    }
)
</script>

<template>
    <div
        class="flex items-center"
        :class="{
            'justify-between': isEditable,
        }"
    >
        <div
            ref="element"
            :class="{
                'mr-2': isEditable,
                'w-full': isEditable,
            }"
        >
            {{ value }}
        </div>
        <div v-if="isEditable" class="flex items-center">
            <button @click.prevent="onCancel" class="mr-2">
                <v-icon icon="fal fa-xmark" color="gray" />
            </button>
            <button @click.prevent="onSave">
                <v-icon icon="fal fa-floppy-disk" color="gray" />
            </button>
        </div>
    </div>
</template>
